import { useRef, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { PAYMENTS_QUERY } from '@/gql/queries';
import { PAYMENTS_PER_PAGE } from '@/pages/YourPaymentsPage/constants';
import { PaymentsQuery, PaymentsUnion } from '@/generated/types';
import { useTranslation } from 'react-i18next';
import { QueryResult, GroupedPayments } from '../types';
import { mapPaymentsQueryResponseToGroupedPayments } from '../mappers';
import { PaymentState, PaymentEnum } from '@/generated/types';

interface ResultData {
  groups: GroupedPayments[];
  payments: PaymentsUnion[];
  totalCount: number;
}

export type UsePaymentsListResult = QueryResult<ResultData> & {
  loadMore: () => void;
  hasMore: boolean;
  infiniteScrollLoading: boolean;
};

type Filters = {
  state?: {
    _eq: PaymentState;
  };
  type?: {
    _eq: PaymentEnum;
  };
};

type UsePaymentsList = (args: {
  subscriptionId?: string;
  device?: string;
  status?: string;
  paymentType?: string;
  isFilterEmptyResult?: boolean;
  isForFailedBanner?: boolean;
}) => UsePaymentsListResult;

type Cursor = string | null;

const getFilters = (status?: string, paymentType?: string) => {
  const filters: Filters = {};
  if (status === 'CANCELED') {
    return {
      state: {
        _eq: PaymentState.FAILED,
      },
      type: {
        _eq: PaymentEnum.ORDER_PAYMENT,
      },
    };
  }

  if (status) {
    filters.state = {
      _eq: status as PaymentState,
    };
  }

  if (paymentType) {
    filters.type = {
      _eq: paymentType as PaymentEnum,
    };
  }
  return filters;
};

export const usePaymentsList: UsePaymentsList = ({
  subscriptionId,
  device,
  status,
  paymentType,
  isFilterEmptyResult,
  isForFailedBanner,
}) => {
  const hasMoreRef = useRef<boolean>();
  const lastCallCursorRef = useRef<string | null>();
  const { t } = useTranslation();
  const [cursor, setCursor] = useState<Cursor>(null);
  const [data, setData] = useState<ResultData>({ groups: [], payments: [], totalCount: 0 });
  const [isPartial, setIsPartial] = useState(false);
  const [loading, setLoading] = useState(true);
  const [infiniteScrollLoading, setInfiniteScrollLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const variables = {
    firstPayments: isForFailedBanner ? 100 : PAYMENTS_PER_PAGE,
    subscriptionId: subscriptionId || (device ? device : undefined),
    cursor,
    where: getFilters(status, paymentType),
  };

  useEffect(() => {
    setIsPartial(false); // filters changed
    if (isFilterEmptyResult) {
      setData({ groups: [], payments: [], totalCount: 0 });
      hasMoreRef.current = false;
    } else {
      setLoading(true);
    }

    if (device || status || paymentType) {
      setCursor(null);
    }

    if (status === 'CANCELED') {
      setLoading(false);
    }
  }, [device, status, paymentType]);

  const { data: payload, error } = useQuery<PaymentsQuery>(PAYMENTS_QUERY, {
    variables,
    skip: isFilterEmptyResult,
    fetchPolicy: 'network-only',
    /**
     * Enables both data and errors to be returned in the response
     * @see https://www.apollographql.com/docs/react/v3/data/error-handling#graphql-error-policies
     * However, "onCompleted" and "onError" hooks can not be used, because only one of them will be called
     * @see https://github.com/apollographql/apollo-client/issues/6966
     * */
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (!payload && !error) return; // still loading
    setLoading(false);
    setInfiniteScrollLoading(false);
    if (error) {
      if (error.networkError || error.clientErrors.length || error.protocolErrors.length)
        return setHasError(true);
      if (error.graphQLErrors.length) {
        if (!payload) return setHasError(true);
        setIsPartial(true);
      }
    }
    if (!payload) return;
    const totalCount = payload.payments?.totalCount ?? 0;
    lastCallCursorRef.current = payload.payments?.pageInfo.endCursor ?? null;
    hasMoreRef.current = payload.payments?.pageInfo.hasNextPage ?? false;

    const hasPreviousPage = payload.payments?.pageInfo.hasPreviousPage;
    setData((current) => {
      let payments = (payload.payments?.nodes as PaymentsUnion[]) || [];
      if (hasPreviousPage) {
        const previouslyLoadedPayments = current?.payments ?? [];
        payments = [...previouslyLoadedPayments, ...payments];
      }
      const groups = mapPaymentsQueryResponseToGroupedPayments(payments, t);
      return { groups, payments, totalCount };
    });
  }, [error, payload, t]);

  const loadMore = () => {
    if (!hasMoreRef.current) {
      return;
    }

    setLoading(true);
    setInfiniteScrollLoading(true);
    setCursor(lastCallCursorRef.current ?? null);
  };

  return {
    data,
    isPartial,
    loading,
    infiniteScrollLoading,
    loadMore,
    hasMore: hasMoreRef.current ?? false,
    hasError,
  };
};
